/**
 * The alerts overlay: one switch per alert kind, nothing else.
 *
 * NOTIF-11/Q54: a game has no mute button. The full list of that game's
 * available alerts is always shown, each toggled on or off individually.
 * No master mute, no "all off" shortcut, no snooze, no follow control.
 */
import { Button, Dialog, Skeleton, Switch } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { useGameAlerts } from "./useGameAlerts";

export const GameAlertsDialog = ({
  eventId,
  open,
  onClose,
}: {
  eventId: string;
  open: boolean;
  onClose: () => void;
}): ReactNode => {
  const { viewState, rows, isRowPending, toggleAlert } = useGameAlerts(eventId);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Alerts for this game"
      description="Pick which moments we tell you about."
      autoFocus={false}
      footer={
        <Button variant="secondary" fullWidth onClick={onClose}>
          Close
        </Button>
      }
    >
      {viewState === "loading" ? (
        <div className="flex flex-col gap-3">
          <Skeleton shape="control" label="Loading this game's alerts" />
          <Skeleton shape="control" />
          <Skeleton shape="control" />
        </div>
      ) : viewState === "error" ? (
        <p className="text-body text-ink-secondary">
          We could not load this game's alerts. Close this and try again.
        </p>
      ) : (
        <div className="flex max-h-100 flex-col gap-1 overflow-y-auto">
          {rows.map((row) => (
            <Switch
              key={row.alertKindKey}
              label={row.label}
              checked={row.isEnabled}
              disabled={isRowPending(row.alertKindKey)}
              onCheckedChange={(next) => {
                toggleAlert(row.alertKindKey, next);
              }}
            />
          ))}
        </div>
      )}
    </Dialog>
  );
};
